import { Fragment, useId, useRef, useState } from "react";
import {
	Control,
	Controller,
	RegisterOptions,
	useFormContext,
	useFormState,
	ValidateResult,
} from "react-hook-form";
import { acceptedFiles } from "../../../../utilities/constants";
import { FieldWrapper } from "../../FieldWrapper/FieldWrapper";
import styles from "./FileUploadField.module.scss";
import { MaterialIcon } from "../../../shared/MaterialIcon/MaterialIcon";
import { useBreakpoint } from "../../../../hooks/useViewportDimensions";
import { formatBytes } from "../../../../utilities/string-helpers";
import clsx from "clsx";

interface FileUploadFieldProps {
	control: Control;
	name: string;
	label: string;
	hideLabel?: boolean;
	className?: string;
	placeholder?: string;
	registerOptions?: RegisterOptions;
	inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
	slotAfterInput?: React.ReactNode;
	ariaDescribedBy?: string;
}

const MAX_SIZE = 10 * 1024 * 1024;
const FILE_LIMIT = 4;

export const FileUploadField = ({
	control,
	name,
	label,
	hideLabel,
	className,
	registerOptions = {},
	inputProps,
	slotAfterInput,
	ariaDescribedBy,
}: FileUploadFieldProps) => {
	const id = useId();
	const helpId = id + "-help";
	const errorId = id + "-error";

	const { register, setValue, trigger } = useFormContext();
	const { errors } = useFormState();

	const [isDragging, setIsDragging] = useState(false);

	registerOptions.validate = {
		allFilesBelowLimit: (files: FileList): ValidateResult => {
			if (!files?.length) return true;
			const valid = Array.from(files).every(file => file.size < MAX_SIZE);
			return valid || "File size should be less than 10MB";
		},
		allFilesSupported: (files: FileList): ValidateResult => {
			if (!files?.length) return true;
			const valid = Array.from(files).every(file => acceptedFiles.includes(file.type));
			return valid || "This file type is unsupported";
		},
		maxFiles: (files: FileList): ValidateResult => {
			if (!files?.length) return true;
			const valid = files.length <= FILE_LIMIT;
			return valid || `You can upload a maximum of ${FILE_LIMIT} files`;
		},
	};

	const inputRef = useRef<HTMLInputElement>(null);

	const isMobile = !useBreakpoint("md");

	return (
		<FieldWrapper
			{...{
				name,
				label,
				hideLabel,
				required: registerOptions?.required as boolean,
				labelFor: id,
				className,
				errorId,
			}}
		>
			<Controller
				control={control}
				name={name}
				rules={registerOptions}
				render={({ field: { onChange, value: valueAny }, fieldState: { error } }) => {
					const value = valueAny as File[] | undefined;
					function manageFileArray(fileList: FileList) {
						if (value && value.length > 0 && fileList && fileList.length > 0) {
							const newFiles = Array.from(fileList).filter(
								file =>
									!value.find(
										existingFile =>
											file.name === existingFile.name && file.size === existingFile.size,
									),
							);
							onChange([...value, ...newFiles]);
						} else {
							onChange([...Array.from(fileList || [])]);
						}
						if (inputRef.current) {
							inputRef.current.value = "";
						}

						trigger(name);
					}

					function removeFile(index: number) {
						onChange(value?.filter((_, i) => i !== index) || []);
						trigger(name, { shouldFocus: false });
					}

					return (
						<div className={styles["file-upload-wrapper"]}>
							<div
								className={clsx(
									styles["file-upload"],
									isDragging && styles["file-upload--dragging"],
									errors[name] && styles["file-upload--error"],
								)}
								onDrop={e => {
									e.preventDefault();
									manageFileArray(e.dataTransfer.files);
									if (isDragging) {
										setIsDragging(false);
									}
								}}
								onDragOver={e => {
									e.preventDefault();

									if (!isDragging) {
										setIsDragging(true);
									}
								}}
								onDragLeave={() => {
									if (isDragging) {
										setIsDragging(false);
									}
								}}
							>
								<input
									style={{ display: "none" }}
									accept=".pdf, .jpg, .jpeg, .png, .heic"
									multiple
									name={name}
									ref={inputRef}
									onChange={e => {
										e.target.files && manageFileArray(e.target.files);
									}}
									type="file"
								/>
								<span className={styles["file-upload__icon"]}>
									<MaterialIcon name="upload" />
								</span>
								<div className={styles["file-upload__heading"]}>
									Drag and drop, or{" "}
									<button
										type="button"
										onClick={e => {
											e.preventDefault();
											inputRef.current?.click();
										}}
									>
										browse your files
									</button>
								</div>
								<div className={styles["file-upload__descriptions"]}>
									<div className={styles["file-upload__description"]}>
										File size must not exceed <strong>{formatBytes(MAX_SIZE, 2)}</strong>
									</div>
									<div className={styles["file-upload__description"]}>
										You can upload{" "}
										{acceptedFiles.map((format, i) => (
											<Fragment key={i}>
												{acceptedFiles.length > 1 && i === acceptedFiles.length - 1 && " and "}
												<strong>
													{i > 0 && i < acceptedFiles.length - 1 && ", "}
													{format.split("/")[1]}
												</strong>
											</Fragment>
										))}{" "}
										files.
									</div>
									<div className={styles["file-upload__description"]}>
										You can upload a maximum of <strong>{FILE_LIMIT} files</strong>
									</div>
								</div>
							</div>

							{value && value.length > 0 && (
								<div className={styles["file-list"]}>
									{value.map((file, i) => (
										<li
											key={i}
											className={styles["file-item"]}
										>
											<MaterialIcon name="article" />
											<span className={styles["file-item__name"]}>
												{formatFilename(file, isMobile ? 10 : 15)}
											</span>
											<span className={styles["file-item__size"]}>{formatBytes(file.size, 2)}</span>
											<button
												className={styles["file-item__delete"]}
												type="button"
												onClick={() => removeFile(i)}
												aria-label={`Delete file: ${file.name}`}
											>
												<MaterialIcon name="cancel" />
											</button>
										</li>
									))}
								</div>
							)}
						</div>
					);
				}}
			/>
		</FieldWrapper>
	);
};

const formatFilename = (file: File, maxLength: number) => {
	const fileName = file.name.slice(0, file.name.lastIndexOf("."));
	const extension = file.name.slice(file.name.lastIndexOf("."));
	let outputName = file.name;

	if (fileName.length > maxLength) {
		outputName = `${fileName.slice(0, maxLength)}...${fileName.slice(
			fileName.length - 1,
			fileName.length,
		)}${extension}`;
	}

	return outputName;
};
